import { ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, List } from "@mui/material";
import { BootstrapTooltip } from "../../../../components/Common/BootstrapTooltip";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Zoom from "@mui/material/Zoom";
import React from "react";

interface SubMenuItem {
  label: string;
  route: string;
}

interface CollapsibleMenuItemProps {
  itemKey: string;
  label: string;
  icon: React.ReactNode;
  isOpen: boolean;
  isExpanded: boolean;
  activeRoutes: string[];
  pathname: string;
  subItems: SubMenuItem[];
  onToggle: () => void;
  onSubItemClick: (route: string) => void;
}

export const CollapsibleMenuItem: React.FC<CollapsibleMenuItemProps> = React.memo(({
  itemKey,
  label,
  icon,
  isOpen,
  isExpanded,
  activeRoutes,
  pathname,
  subItems,
  onToggle,
  onSubItemClick
}) => {
  const isActive = activeRoutes.includes(pathname);

  return (
    <ListItem
      key={itemKey}
      disablePadding
      className="nav-ListItemButton"
      sx={{ display: "block", cursor: "pointer" }}
      onClick={onToggle}
    >
      <ListItemButton
        sx={{
          minHeight: 24,
          justifyContent: isOpen ? "initial" : "center",
          px: 0.5,
          borderRadius: "12px",
          "&:hover": {
            backgroundColor: "#e9eefa",
            color: "#3b75ff",
          },
          "& .MuiListItemIcon-root": {
            pointerEvents: "none"
          },
          "& .MuiListItemText-root": {
            pointerEvents: "none"
          }
        }}
      >
        <ListItemIcon
          className="nav-ListItemButton"
          sx={{
            minWidth: 0,
            mr: isOpen ? 3 : "auto",
            justifyContent: "center",
          }}
        >
          {isOpen ? (
            <span className="menu-icon-span">
              {React.cloneElement(icon as React.ReactElement, {
                className: isActive ? "active-master-class-icon menu-icon" : "menu-icon"
              })}
            </span>
          ) : (
            <BootstrapTooltip
              TransitionComponent={Zoom}
              title={<span style={{ fontSize: "12px" }}>{label}</span>}
              placement="right"
            >
              <span className="menu-icon-span">
                {React.cloneElement(icon as React.ReactElement, {
                  className: isActive ? "active-master-class-icon menu-icon" : "menu-icon"
                })}
              </span>
            </BootstrapTooltip>
          )}
        </ListItemIcon>

        <ListItemText
          primary={label}
          sx={{ opacity: isOpen ? 1 : 0 }}
          className={isActive ? "active-master-class-icon menu-item-text" : "menu-item-text"}
        />
        {isExpanded ? (
          <ExpandLess
            sx={{
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#e9eefa",
                color: "#3b75ff",
              },
            }}
            className={isActive ? "active-master-class-icon menu-item-icon-more" : "menu-item-icon-more"}
          />
        ) : (
          <ExpandMore
            className={isActive ? "active-master-class-icon menu-item-icon-more" : "menu-item-icon-more"}
          />
        )}
      </ListItemButton>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {subItems.map((item) => (
            <ListItemButton
              key={item.route}
              className={item.route === pathname ? "active-master-class sub-menu" : "sub-menu"}
              sx={{
                pl: 4,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#e9eefa",
                  color: "#3b75ff",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSubItemClick(item.route);
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </ListItem>
  );
});

CollapsibleMenuItem.displayName = "CollapsibleMenuItem";
